import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Pencil, Trash2, Building2, AlertTriangle, Droplets, Wind, Flame, ArrowDownToLine, CheckCircle, RefreshCw, FileDown, Search } from 'lucide-react';
import { SortSelect, sortByField } from '../../components/SortSelect';
import { ExportDialog } from '../../components/ExportDialog';
import { useExport } from '../../hooks/useExport';
import { exportTableToPDF } from '../../utils/html2pdfExport';
import * as XLSX from 'xlsx';
import { useServerList } from '../../hooks/useServerList';
import Pagination from '../../components/Pagination';
import { TESIS_TURU_OPTIONS as TESIS_TURU_SHARED, tesisTuruText, tesisTuruMatches } from '../../utils/tesisTuru';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PK_BINA_EXPORT_COLUMNS = [
  { key: 'sno', label: 'S.NO' },
  { key: 'bina_adi', label: 'Bina Adı' },
  { key: 'il', label: 'İl' },
  { key: 'ilce', label: 'İlçe' },
  { key: 'mahalle', label: 'Mahalle' },
  { key: 'pafta', label: 'Pafta' },
  { key: 'ada', label: 'Ada' },
  { key: 'parsel', label: 'Parsel' },
  { key: 'adres', label: 'Adres' },
  { key: 'tesis_turu', label: 'Tesis Türü' },
  { key: 'alt_tesis_turu', label: 'Alt Tesis Türü' },
  { key: 'yapim_yili', label: 'Yapım Yılı' },
  { key: 'kullanim_amaci', label: 'Kullanım Amacı' },
  { key: 'tehlike_sinifi', label: 'Tehlike Sınıfı' },
  { key: 'yukseklik_metre', label: 'Yükseklik (m)' },
  { key: 'kat_sayisi', label: 'Kat Sayısı' },
  { key: 'toplam_alan_m2', label: 'Toplam Alan (m²)' },
  { key: 'kapali_otopark_alani_m2', label: 'Kapalı Otopark (m²)' },
  { key: 'sprinkler_zorunlu', label: 'Sprinkler Zorunlu' },
  { key: 'yangin_alarm_zorunlu', label: 'Yangın Alarmı Zorunlu' },
  { key: 'duman_kontrol_zorunlu', label: 'Duman Kontrol Zorunlu' },
  { key: 'otopark_duman_tahliye_zorunlu', label: 'Otopark Duman Tahliye' },
  { key: 'emlak_kaynagi', label: 'Emlak\'tan Aktarıldı' },
  { key: 'emlak_son_senkronizasyon', label: 'Son Senkronizasyon' },
];

const EMPTY_FORM = {
  bina_adi: '', adres: '', il: 'İstanbul', ilce: '', koordinat_lat: '', koordinat_lng: '',
  kullanim_amaci: 'konut_disi', yukseklik_metre: '', toplam_alan_m2: '',
  kapali_otopark_alani_m2: '', kat_sayisi: '', tehlike_sinifi: 'az_tehlikeli'
};

export default function PKBinalar() {
  const { user } = useAuth();
  const isViewer = !!user?.is_viewer;
  const [binalar, setBinalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({...EMPTY_FORM});
  const [submitting, setSubmitting] = useState(false);
  const [sortKey, setSortKey] = useState('bina_adi_az');
  const [tesisTuruFilter, setTesisTuruFilter] = useState('all');
  const [emlakDialogOpen, setEmlakDialogOpen] = useState(false);
  const [emlakYapilar, setEmlakYapilar] = useState([]);
  const [aktarilanIds, setAktarilanIds] = useState(new Set());
  const [aktarmaLoading, setAktarmaLoading] = useState(false);
  const [guncelleLoading, setGuncelleLoading] = useState(false);
  const [guncelleSonuc, setGuncelleSonuc] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { selectedColumns, pdfPageSize, setPdfPageSize, toggleColumn, selectAll, clearAll } = useExport('a4', PK_BINA_EXPORT_COLUMNS.map(c => c.key));

  // Server-side liste (görüntülenen sayfa için)
  const SORT_MAP_BINA = {
    bina_adi_az: 'bina_adi:asc', bina_adi_za: 'bina_adi:desc',
    ilce_az: 'ilce:asc',
    alan_buyuk: 'toplam_alan_m2:desc', alan_kucuk: 'toplam_alan_m2:asc',
  };
  const binaList = useServerList('/pk/binalar', {
    pageSize: 100,
    initialSort: 'bina_adi:asc',
  });
  useEffect(() => {
    binaList.setSort(SORT_MAP_BINA[sortKey] || 'bina_adi:asc');
  }, [sortKey]);// eslint-disable-line
  useEffect(() => {
    binaList.setFilter('tesis_turu', tesisTuruFilter === 'all' ? undefined : tesisTuruFilter);
  }, [tesisTuruFilter]);// eslint-disable-line

  useEffect(() => { fetchBinalar(); }, []);

  const fetchBinalar = async () => {
    try {
      // Full liste sadece aktarilanIds + export için lazım
      const res = await axios.get(`${API}/pk/binalar`, { withCredentials: true });
      setBinalar(res.data);
      const ids = new Set(res.data.filter(b => b.emlak_kaynak_id).map(b => b.emlak_kaynak_id));
      setAktarilanIds(ids);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const refetchAll = () => { fetchBinalar(); binaList.refetch(); };

  const fetchEmlakYapilar = async () => {
    try {
      const res = await axios.get(`${API}/pk/emlak-yapilar`, { withCredentials: true });
      setEmlakYapilar(res.data);
    } catch (err) { console.error(err); }
  };

  const handleEmlakAktar = async (emlakId) => {
    setAktarmaLoading(true);
    try {
      await axios.post(`${API}/pk/binalar/emlaktan-aktar`, { emlak_id: emlakId }, { withCredentials: true });
      setAktarilanIds(prev => new Set([...prev, emlakId]));
      refetchAll();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      // "Zaten aktarılmış" durumu: işaretle, sessizce ekrana yansıt
      if (typeof detail === 'string' && detail.toLowerCase().includes('zaten')) {
        setAktarilanIds(prev => new Set([...prev, emlakId]));
        await fetchBinalar();
        binaList.refetch();
      } else {
        alert('Hata: ' + detail);
      }
    } finally { setAktarmaLoading(false); }
  };

  const handleEmlaktanGuncelle = async () => {
    if (!window.confirm('Emlak modülündeki güncel veriler ile aktarılmış tüm binaların bilgileri güncellenecek. Manuel girilen alanlar (yükseklik, kat sayısı, otopark alanı, tehlike sınıfı) korunacak. Devam edilsin mi?')) return;
    setGuncelleLoading(true);
    setGuncelleSonuc(null);
    try {
      const res = await axios.post(`${API}/pk/binalar/emlaktan-guncelle`, {}, { withCredentials: true });
      setGuncelleSonuc(res.data);
      await fetchBinalar();
      await fetchEmlakYapilar();
      binaList.refetch();
    } catch (err) {
      setGuncelleSonuc({ hata: err.response?.data?.detail || err.message });
    } finally {
      setGuncelleLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.bina_adi) return;
    setSubmitting(true);
    try {
      if (editMode) {
        await axios.put(`${API}/pk/binalar/${editId}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/pk/binalar`, formData, { withCredentials: true });
      }
      setDialogOpen(false);
      setFormData({...EMPTY_FORM});
      refetchAll();
    } catch (err) { alert('Hata: ' + (err.response?.data?.detail || err.message)); }
    finally { setSubmitting(false); }
  };

  const handleEdit = (bina) => {
    setFormData(bina);
    setEditId(bina.id);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu binayı silmek istediğinizden emin misiniz?')) return;
    await axios.delete(`${API}/pk/binalar/${id}`, { withCredentials: true });
    refetchAll();
  };

  // Otomatik zorunluluk hesaplama (preview)
  const yukseklik = parseFloat(formData.yukseklik_metre || 0);
  const otopark = parseFloat(formData.kapali_otopark_alani_m2 || 0);
  const isKonutDisi = formData.kullanim_amaci === 'konut_disi';
  const sprinklerZorunlu = isKonutDisi && yukseklik > 21.5;
  const otoparkSprinkler = otopark > 600;
  const otoparkDuman = otopark > 2000;

  const SORT_DEFS = { bina_adi_az: { field: 'bina_adi', dir: 1 }, bina_adi_za: { field: 'bina_adi', dir: -1 }, ilce_az: { field: 'ilce', dir: 1 }, alan_buyuk: { field: 'toplam_alan_m2', dir: -1, type: 'number' }, alan_kucuk: { field: 'toplam_alan_m2', dir: 1, type: 'number' } };

  // Emlak modulundeki ile birebir ayni tesis turleri (ortak util)
  const TESIS_TURU_OPTIONS = TESIS_TURU_SHARED.map(o => ({ v: o.value, l: o.label }));

  // Tesis Türü filtreleme: 'all' -> hepsi, 'bos' -> tesis_turu olmayan kayitlar
  // Görüntülenen liste: server-side paginated
  const sortedBinalar = binaList.items;
  // Export için: tüm filtrelenmiş binalar (client-side, full liste üzerinde)
  const filteredBinalarFull = binalar.filter((b) => tesisTuruFilter === 'all' || tesisTuruMatches(b.tesis_turu, [tesisTuruFilter]));
  const sortedBinalarFull = sortByField(filteredBinalarFull, sortKey, SORT_DEFS);

  // "Son güncellenme" göreceli zaman formatlaması (Türkçe)
  const formatGoreceli = (iso) => {
    if (!iso) return null;
    try {
      const t = new Date(iso).getTime();
      if (isNaN(t)) return null;
      const diff = Date.now() - t;
      const dakika = Math.floor(diff / 60000);
      if (dakika < 1) return 'az önce';
      if (dakika < 60) return `${dakika} dk önce`;
      const saat = Math.floor(dakika / 60);
      if (saat < 24) return `${saat} sa önce`;
      const gun = Math.floor(saat / 24);
      if (gun < 30) return `${gun} gün önce`;
      const ay = Math.floor(gun / 30);
      if (ay < 12) return `${ay} ay önce`;
      return `${Math.floor(ay / 12)} yıl önce`;
    } catch { return null; }
  };

  // Export satırı oluşturucu
  const buildExportRow = (bina, idx) => {
    const yesHayir = (v) => (v ? 'Evet' : 'Hayır');
    const kullanimMap = { konut: 'Konut', konut_disi: 'Konut Dışı (Kamu)' };
    const tehlikeMap = { az_tehlikeli: 'Az Tehlikeli', tehlikeli: 'Tehlikeli', cok_tehlikeli: 'Çok Tehlikeli' };
    const tesisLabel = tesisTuruText(bina.tesis_turu) || '-';
    return {
      sno: idx + 1,
      bina_adi: bina.bina_adi || '-',
      il: bina.il || '-',
      ilce: bina.ilce || '-',
      mahalle: bina.mahalle || '-',
      pafta: bina.pafta || '-',
      ada: bina.ada || '-',
      parsel: bina.parsel || '-',
      adres: bina.adres || '-',
      tesis_turu: tesisLabel,
      alt_tesis_turu: bina.alt_tesis_turu || '-',
      yapim_yili: bina.yapim_yili || '-',
      kullanim_amaci: kullanimMap[bina.kullanim_amaci] || bina.kullanim_amaci || '-',
      tehlike_sinifi: tehlikeMap[bina.tehlike_sinifi] || bina.tehlike_sinifi || '-',
      yukseklik_metre: bina.yukseklik_metre ?? '-',
      kat_sayisi: bina.kat_sayisi ?? '-',
      toplam_alan_m2: bina.toplam_alan_m2 ?? '-',
      kapali_otopark_alani_m2: bina.kapali_otopark_alani_m2 ?? '-',
      sprinkler_zorunlu: yesHayir(bina.sprinkler_zorunlu),
      yangin_alarm_zorunlu: yesHayir(bina.yangin_alarm_zorunlu),
      duman_kontrol_zorunlu: yesHayir(bina.duman_kontrol_zorunlu),
      otopark_duman_tahliye_zorunlu: yesHayir(bina.otopark_duman_tahliye_zorunlu),
      emlak_kaynagi: bina.emlak_kaynak_id ? 'Evet' : 'Hayır',
      emlak_son_senkronizasyon: bina.emlak_son_senkronizasyon
        ? new Date(bina.emlak_son_senkronizasyon).toLocaleString('tr-TR')
        : '-',
    };
  };

  const runExport = async (format) => {
    if (selectedColumns.length === 0) {
      alert('En az bir sütun seçmelisiniz.');
      return;
    }
    setExporting(true);
    try {
      const headers = selectedColumns.map(k => PK_BINA_EXPORT_COLUMNS.find(c => c.key === k)?.label || k);
      const data = sortedBinalarFull.map((b, idx) => {
        const row = buildExportRow(b, idx);
        return selectedColumns.map(k => String(row[k] ?? '-'));
      });
      if (format === 'pdf') {
        await exportTableToPDF(
          headers,
          data,
          'PK Bina Yönetimi',
          'pk_bina_yonetimi.pdf',
          { pageSize: pdfPageSize }
        );
      } else {
        const wsData = [headers, ...data];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 2, 14) }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'PK Binalar');
        XLSX.writeFile(wb, 'pk_bina_yonetimi.xlsx');
      }
      setExportDialogOpen(false);
    } catch (err) {
      alert('Export hatası: ' + (err.message || err));
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="text-gray-400 text-center py-8">Yükleniyor...</div>;

  return (
    <div className="space-y-6" data-testid="pk-binalar">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Bina Yönetimi</h2>
          <p className="text-sm text-gray-400">{binaList.total} bina {binaList.loading && '· yükleniyor...'}{tesisTuruFilter !== 'all' && ' (filtreli)'}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={binaList.search}
              onChange={(e) => binaList.setSearch(e.target.value)}
              placeholder="Bina adı, ilçe, mahalle, adres..."
              className="bg-gray-800 border-gray-700 text-white pl-8 h-9 w-[240px]"
              data-testid="bina-search-input"
            />
          </div>
          <SortSelect value={sortKey} onChange={setSortKey} customOptions={[
            { value: 'bina_adi_az', label: 'Bina Adı (A-Z)' }, { value: 'bina_adi_za', label: 'Bina Adı (Z-A)' },
            { value: 'ilce_az', label: 'İlçe (A-Z)' },
            { value: 'alan_buyuk', label: 'Alan (Büyük→Küçük)' }, { value: 'alan_kucuk', label: 'Alan (Küçük→Büyük)' }
          ]} />
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Tesis Türü:</span>
            <Select value={tesisTuruFilter} onValueChange={setTesisTuruFilter}>
              <SelectTrigger
                className="bg-gray-800 border-gray-700 text-white h-9 w-[180px] text-sm"
                data-testid="tesis-turu-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-[320px]">
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="bos">(Tesis türü boş)</SelectItem>
                {TESIS_TURU_OPTIONS.map((opt) => (
                  <SelectItem key={opt.v} value={opt.v}>{opt.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => { fetchEmlakYapilar(); setEmlakDialogOpen(true); }} variant="outline" className="bg-emerald-900/30 border-emerald-700/50 text-emerald-400 hover:bg-emerald-900/50" data-testid="emlak-aktar-btn">
            <ArrowDownToLine className="w-4 h-4 mr-2" /> Emlak'tan Aktar
          </Button>
          <Button
            onClick={() => setExportDialogOpen(true)}
            variant="outline"
            className="bg-blue-900/30 border-blue-700/50 text-blue-300 hover:bg-blue-900/50"
            data-testid="pk-binalar-export-btn"
          >
            <FileDown className="w-4 h-4 mr-2" /> Yazdır (PDF/Excel)
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedBinalar.map(bina => (
          <Card key={bina.id} className="bg-gray-900/80 border-gray-700/50 hover:border-gray-600 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-400" />
                  <h3 className="text-sm font-semibold text-white">{bina.bina_adi}</h3>
                </div>
                <div className="flex gap-1">
                  {!isViewer && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(bina)} className="h-7 w-7 p-0 text-gray-400 hover:text-white"><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(bina.id)} className="h-7 w-7 p-0 text-gray-400 hover:text-red-400"><Trash2 className="w-3 h-3" /></Button>
                    </>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-1">{bina.il} / {bina.ilce}</p>
              {bina.emlak_kaynak_id && (
                <div className="flex items-center gap-1.5 flex-wrap mt-1">
                  <Badge className="bg-emerald-900/40 text-emerald-300 text-[10px]">Emlak'tan Aktarıldı</Badge>
                  {bina.emlak_son_senkronizasyon && (
                    <span
                      className="text-[10px] text-blue-300/80 bg-blue-900/20 border border-blue-800/40 rounded px-1.5 py-0.5 flex items-center gap-1"
                      title={`Son güncellenme: ${new Date(bina.emlak_son_senkronizasyon).toLocaleString('tr-TR')}`}
                      data-testid={`son-guncelleme-${bina.id}`}
                    >
                      <RefreshCw className="w-2.5 h-2.5" />
                      Son güncelleme: {formatGoreceli(bina.emlak_son_senkronizasyon)}
                    </span>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                <div><span className="text-gray-500">Yükseklik:</span> <span className="text-white">{bina.yukseklik_metre}m</span></div>
                <div><span className="text-gray-500">Kat:</span> <span className="text-white">{bina.kat_sayisi}</span></div>
                <div><span className="text-gray-500">Alan:</span> <span className="text-white">{bina.toplam_alan_m2} m²</span></div>
                <div><span className="text-gray-500">Otopark:</span> <span className="text-white">{bina.kapali_otopark_alani_m2 || 0} m²</span></div>
              </div>
              {/* Zorunluluk badge'leri */}
              <div className="flex flex-wrap gap-1 mt-3">
                {bina.sprinkler_zorunlu && <Badge className="bg-orange-900/40 text-orange-300 text-[10px]"><Droplets className="w-3 h-3 mr-1" />Sprinkler</Badge>}
                {bina.yangin_alarm_zorunlu && <Badge className="bg-red-900/40 text-red-300 text-[10px]"><Flame className="w-3 h-3 mr-1" />Yangın Alarm</Badge>}
                {bina.duman_kontrol_zorunlu && <Badge className="bg-purple-900/40 text-purple-300 text-[10px]"><Wind className="w-3 h-3 mr-1" />Duman Kontrol</Badge>}
                {bina.otopark_duman_tahliye_zorunlu && <Badge className="bg-indigo-900/40 text-indigo-300 text-[10px]">Otopark Duman</Badge>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Server-side pagination */}
      <Pagination
        page={binaList.page}
        totalPages={binaList.totalPages}
        total={binaList.total}
        pageSize={binaList.pageSize}
        loading={binaList.loading}
        onPageChange={binaList.setPage}
        onPageSizeChange={binaList.setPageSize}
        testIdPrefix="bina-pagination"
      />

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => setDialogOpen(v)}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="bina-form-title">{editMode ? 'Bina Düzenle' : 'Yeni Bina Ekle'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Bina Adı *</Label>
              <Input value={formData.bina_adi} onChange={e => setFormData({...formData, bina_adi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
            </div>
            <div className="space-y-2">
              <Label>Adres</Label>
              <Input value={formData.adres} onChange={e => setFormData({...formData, adres: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>İl</Label>
                <Input value={formData.il} onChange={e => setFormData({...formData, il: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label>İlçe</Label>
                <Input value={formData.ilce} onChange={e => setFormData({...formData, ilce: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kullanım Amacı</Label>
                <Select value={formData.kullanim_amaci} onValueChange={v => setFormData({...formData, kullanim_amaci: v})}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="konut_disi">Konut Dışı (Kamu)</SelectItem>
                    <SelectItem value="konut">Konut</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tehlike Sınıfı</Label>
                <Select value={formData.tehlike_sinifi} onValueChange={v => setFormData({...formData, tehlike_sinifi: v})}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="az_tehlikeli">Az Tehlikeli</SelectItem>
                    <SelectItem value="tehlikeli">Tehlikeli</SelectItem>
                    <SelectItem value="cok_tehlikeli">Çok Tehlikeli</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Yükseklik (m)</Label>
                <Input type="number" value={formData.yukseklik_metre} onChange={e => setFormData({...formData, yukseklik_metre: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label>Toplam Alan (m²)</Label>
                <Input type="number" value={formData.toplam_alan_m2} onChange={e => setFormData({...formData, toplam_alan_m2: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label>Kat Sayısı</Label>
                <Input type="number" value={formData.kat_sayisi} onChange={e => setFormData({...formData, kat_sayisi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Kapalı Otopark Alanı (m²)</Label>
              <Input type="number" value={formData.kapali_otopark_alani_m2} onChange={e => setFormData({...formData, kapali_otopark_alani_m2: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="0" />
            </div>

            {/* Otomatik Zorunluluk Bildirimi */}
            {(sprinklerZorunlu || otoparkSprinkler || otoparkDuman) && (
              <div className="p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg space-y-1">
                <p className="text-xs font-semibold text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Otomatik Zorunluluk Tespiti:</p>
                {sprinklerZorunlu && <p className="text-xs text-amber-300">Yüksek bina ({yukseklik}m &gt; 21.5m): Sprinkler, Yangın Algılama, Duman Kontrol ve Yangın Kontrol Merkezi zorunlu.</p>}
                {otoparkSprinkler && <p className="text-xs text-amber-300">Otopark &gt; 600 m²: Otopark sprinkler zorunlu.</p>}
                {otoparkDuman && <p className="text-xs text-amber-300">Otopark &gt; 2000 m²: Mekanik duman tahliye sistemi zorunlu (min 10 hava değişimi/saat).</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => { setDialogOpen(false); setFormData({...EMPTY_FORM}); }} variant="ghost" className="text-gray-400">İptal</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700">{submitting ? 'Kaydediliyor...' : editMode ? 'Güncelle' : 'Oluştur'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Emlak'tan Aktar Dialog */}
      <Dialog open={emlakDialogOpen} onOpenChange={(v) => { setEmlakDialogOpen(v); if (!v) setGuncelleSonuc(null); }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3 pr-8">
              <DialogTitle data-testid="emlak-aktar-title">Emlak İşlemleri'nden Yapı Aktar</DialogTitle>
              <Button
                size="sm"
                onClick={handleEmlaktanGuncelle}
                disabled={guncelleLoading || aktarilanIds.size === 0}
                className="bg-blue-600 hover:bg-blue-700 h-8 text-xs disabled:opacity-50"
                data-testid="emlak-verilerini-guncelle-btn"
                title={aktarilanIds.size === 0 ? 'Henüz aktarılmış bina yok' : 'Aktarılmış binaları Emlak\'taki güncel verilerle senkronize eder'}
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${guncelleLoading ? 'animate-spin' : ''}`} />
                {guncelleLoading ? 'Güncelleniyor...' : 'Emlak Verilerini Güncelle'}
              </Button>
            </div>
          </DialogHeader>
          <p className="text-xs text-gray-400 mb-3">Emlak-Kamulaştırma modülünde "Yapı" olarak kayıtlı taşınmazları PK Bina Yönetimi'ne aktarın.</p>

          {guncelleSonuc && (
            <div
              className={`mb-4 p-3 rounded-lg border text-xs ${
                guncelleSonuc.hata
                  ? 'bg-red-900/20 border-red-700/50 text-red-300'
                  : 'bg-emerald-900/20 border-emerald-700/50 text-emerald-300'
              }`}
              data-testid="emlak-guncelle-sonuc"
            >
              {guncelleSonuc.hata ? (
                <span>Hata: {guncelleSonuc.hata}</span>
              ) : (
                <div className="space-y-1">
                  <p className="font-semibold flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" /> {guncelleSonuc.mesaj}
                  </p>
                  {guncelleSonuc.detaylar?.length > 0 && (
                    <ul className="list-disc list-inside text-[11px] text-emerald-200/80 max-h-32 overflow-y-auto">
                      {guncelleSonuc.detaylar.map((d, i) => (
                        <li key={i}>
                          {d.bina_adi} – {d.durum === 'guncellendi' ? 'güncellendi' : 'kaynak Emlak kaydı silinmiş'}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
          {emlakYapilar.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <Building2 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm">Emlak İşlemleri'nde "Yapı" türünde kayıt bulunamadı.</p>
              <p className="text-xs text-gray-600 mt-1">Önce Emlak-Kamulaştırma modülünden yapı kaydı eklemeniz gerekiyor.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {emlakYapilar.map(y => {
                const zatenAktarildi = aktarilanIds.has(y.id);
                const adaParsel = [y.ada && `Ada: ${y.ada}`, y.parsel && `Parsel: ${y.parsel}`].filter(Boolean).join(' / ');
                return (
                  <div key={y.id} className={`p-3 rounded-lg border ${zatenAktarildi ? 'bg-green-900/10 border-green-800/30' : 'bg-gray-800/50 border-gray-700/50 hover:border-gray-600'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white">{y.tesis_adi || `${tesisTuruText(y.tesis_turu) || 'Yapı'} - ${y.ilce || '?'}`}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-400 mt-1 flex-wrap">
                          <span>{y.il || 'İstanbul'} / {y.ilce || '—'}</span>
                          {y.mahalle && <span className="text-gray-500">Mah: {y.mahalle}</span>}
                          {tesisTuruText(y.tesis_turu) && <Badge className="bg-blue-900/40 text-blue-300 text-[10px] px-1.5 py-0">{tesisTuruText(y.tesis_turu)}</Badge>}
                        </div>
                        <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-1 flex-wrap">
                          {adaParsel && <span>{adaParsel}</span>}
                          {y.alan_m2 && <span>Arsa: {Number(y.alan_m2).toLocaleString('tr-TR')} m²</span>}
                          {y.insaat_alani_m2 && <span>İnşaat: {Number(y.insaat_alani_m2).toLocaleString('tr-TR')} m²</span>}
                          {y.cati_alani_m2 && <span>Çatı: {Number(y.cati_alani_m2).toLocaleString('tr-TR')} m²</span>}
                          {y.yapim_yili && <span>Yapım: {y.yapim_yili}</span>}
                          {y.mulkiyet && <span className="text-amber-400/70">{y.mulkiyet}</span>}
                        </div>
                        {y.kamu_kurum_adi && <p className="text-[10px] text-gray-600 mt-0.5">Kurum: {y.kamu_kurum_adi}</p>}
                      </div>
                      <div className="flex-shrink-0 pt-1">
                        {zatenAktarildi ? (
                          <Badge className="bg-green-800 text-green-200 text-[10px] flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Aktarıldı
                          </Badge>
                        ) : (
                          <Button size="sm" onClick={() => handleEmlakAktar(y.id)} disabled={aktarmaLoading}
                            className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" data-testid={`aktar-${y.id}`}>
                            <ArrowDownToLine className="w-3 h-3 mr-1" /> Aktar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setEmlakDialogOpen(false)} className="bg-gray-700 hover:bg-gray-600">Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog (PDF/Excel) */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        columns={PK_BINA_EXPORT_COLUMNS}
        selectedColumns={selectedColumns}
        onToggleColumn={toggleColumn}
        onSelectAll={() => selectAll(PK_BINA_EXPORT_COLUMNS)}
        onClearAll={clearAll}
        pdfPageSize={pdfPageSize}
        onPdfPageSizeChange={setPdfPageSize}
        onExportPDF={() => !exporting && runExport('pdf')}
        onExportExcel={() => !exporting && runExport('excel')}
        title="Sütun Seçimi (PDF & Excel)"
        description={`PK Bina Yönetimi - ${sortedBinalarFull.length} kayıt dışa aktarılacak${tesisTuruFilter !== 'all' ? ' (filtreli)' : ''}`}
        gridCols={3}
        idPrefix="pk-binalar-export"
      />
    </div>
  );
}
